import { MouseEvent, ReactNode } from 'react';
import classNames from 'classnames';
import { cva } from 'class-variance-authority';
import { TargetAudienceTypeEnum } from '@employ-engage/data';

// CVA for pills and list
const pillListCva = cva(
  'flex flex-wrap px-1 pb-1 border border-gray-300 dark:border-dark-secondaryText rounded bg-gray-100 min-h-[36px]'
);

const pillCva = cva('px-1 border rounded mr-2 mt-1 flex items-center', {
  variants: {
    bgColor: {
      default: 'bg-white',
      [TargetAudienceTypeEnum.ROLE]: 'bg-blue-100',
      [TargetAudienceTypeEnum.ORGANIZATION]: 'bg-green-100',
      [TargetAudienceTypeEnum.INDIVIDUAL]: 'bg-yellow-100',
    },
    textColor: {
      default: 'text-light-secondaryText',
      [TargetAudienceTypeEnum.ROLE]: 'text-blue-500',
      [TargetAudienceTypeEnum.ORGANIZATION]: 'text-green-500',
      [TargetAudienceTypeEnum.INDIVIDUAL]: 'text-yellow-500',
    },
    borderColor: {
      default: 'border-light-secondaryText',
      [TargetAudienceTypeEnum.ROLE]: 'border-blue-500',
      [TargetAudienceTypeEnum.ORGANIZATION]: 'border-green-500',
      [TargetAudienceTypeEnum.INDIVIDUAL]: 'border-yellow-500',
    },
  },
  compoundVariants: [
    {
      bgColor: undefined,
      class: 'bg-white',
    },
    {
      textColor: undefined,
      class: 'text-light-secondaryText',
    },
    {
      borderColor: undefined,
      class: 'border-light-secondaryText',
    },
  ],
  defaultVariants: {
    bgColor: 'default',
    textColor: 'default',
    borderColor: 'default',
  },
});

export type ObjectPillListItem = {
  label: string;
  [key: string]: any;
};

export type OnClickXMarkForObjectPillListItem = (
  list: any[],
  item: any
) => void;

export interface ObjectPillListProps {
  leadingIcon?: ReactNode;
  list: any[];
  onClickXMark: OnClickXMarkForObjectPillListItem;
  variant?: TargetAudienceTypeEnum;
  className?: string;
  testId?: string;
  mapItem?: (item: any) => ObjectPillListItem;
}

export function ObjectPillList({
  leadingIcon,
  list = [],
  onClickXMark,
  className,
  testId,
  mapItem,
  variant: globalVariant,
}: ObjectPillListProps) {
  const renderObjectPill = (item: ObjectPillListItem, index: number) => {
    const { key, label } = mapItem
      ? mapItem(item)
      : { key: item.label, label: item.label };

    const handleClick = (
      e: MouseEvent<HTMLButtonElement>,
      item: ObjectPillListItem
    ) => {
      e.stopPropagation();
      const onClickXMarkForObjectPillListItem =
        onClickXMark as OnClickXMarkForObjectPillListItem;

      onClickXMarkForObjectPillListItem(list, item);
    };

    // Use item.type, item.variant, or fallback to globalVariant, then 'default'
    const variant = item.type || item.variant || globalVariant || 'default';

    const pillClass = pillCva({
      bgColor: variant,
      textColor: variant,
      borderColor: variant,
    });

    return (
      <li
        data-testid={testId ? `${testId}-pill-${index}` : undefined}
        className={pillClass}
        key={key}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
        }}
      >
        {label}{' '}
        <button type="button" onClick={(e) => handleClick(e, item)}>
          <i className="fa-solid fa-circle-xmark cursor-pointer" />
        </button>
      </li>
    );
  };

  return (
    <div className={classNames('mt-1', className)}>
      <ul
        data-testid={testId ? `${testId}-pill-list` : undefined}
        className={pillListCva()}
      >
        {leadingIcon && <li className="px-1 mr-2 mt-1">{leadingIcon}</li>}
        {list.map(renderObjectPill)}
      </ul>
    </div>
  );
}

export default ObjectPillList;
